import { useMemo } from 'react'
import {
  GRAPH_LABEL_FADE_DURATION_MS,
  LOSS_OF_CONTROL_TRANSITION_DURATION_MS,
  RISK_BACKGROUND_LABEL_GUIDE_STROKE,
} from './graphConstants'
import { getLabelStroke } from './renderUtils'
import { LossOfControlGradientStrip } from './LossOfControlGradientStrip'
import {
  RedAboveLossOfControlOverlay,
  type FadingCapabilityLinePolylines,
  type PlotSpanX,
  type ScreenPoint,
} from './RedAboveLossOfControlOverlay'
import { useLossOfControlEditor } from './LossOfControlEditorProvider'
import { createSmoothedScreenPoints } from './graphUtils'
import type { PlanId } from '@/lib/branchTypes'
import type { GraphPoint } from './types'

export type RiskBackgroundLabel = {
  label: string
  x: number
  y: number
  guidePathData: string
  opacity: number
}

export const getLossOfControlAccentColor = () => 'var(--race-background)'

export const ExtrapolatedGraphLossOfControlLayer = ({
  showLossOfControlLines,
  redAboveLossOfControl,
  redAboveLossOfControlCanvasLayerRendered = false,
  redAboveLossOfControlFaded = false,
  activePlan,
  capabilityLinePoints,
  capabilityLineSmoothing,
  additionalCapabilityLinePolylines = [],
  fadingCapabilityLinePolylines = null,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
  valueForY,
  showTopDashedLine,
  lossOfControlAccentColor,
  riskBackgroundLabels,
  plotSpanX,
}: {
  showLossOfControlLines: boolean
  redAboveLossOfControl: boolean
  redAboveLossOfControlCanvasLayerRendered?: boolean
  redAboveLossOfControlFaded?: boolean
  activePlan: PlanId
  capabilityLinePoints: GraphPoint[]
  capabilityLineSmoothing: number
  additionalCapabilityLinePolylines?: ScreenPoint[][]
  fadingCapabilityLinePolylines?: FadingCapabilityLinePolylines | null
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  valueForY: (screenY: number) => number
  showTopDashedLine: boolean
  lossOfControlAccentColor: string
  riskBackgroundLabels: RiskBackgroundLabel[]
  // Forwarded to the dither overlay so its hovered-band highlight tiles use
  // the same edge-flush column grid as the static canvas dither.
  plotSpanX?: PlotSpanX
}) => {
  const { editorMode } = useLossOfControlEditor()

  const capabilityLineScreenPoints = useMemo(
    () => createSmoothedScreenPoints(
      capabilityLinePoints.map(graphPoint => ({
        x: xForTimestamp(graphPoint.timestamp),
        y: yForValue(graphPoint.value),
      })),
      capabilityLineSmoothing
    ),
    [capabilityLinePoints, capabilityLineSmoothing, xForTimestamp, yForValue],
  )

  return (
  <>
    <LossOfControlGradientStrip
      show={showLossOfControlLines}
      lossOfControlMinPoints={lossOfControlMinPoints}
      lossOfControlMaxPoints={lossOfControlMaxPoints}
      effectiveCappedTimestamp={effectiveCappedTimestamp}
      xForTimestamp={xForTimestamp}
      yForValue={yForValue}
      showTopDashedLine={showTopDashedLine}
      lineColor={lossOfControlAccentColor}
      transitionDurationMs={LOSS_OF_CONTROL_TRANSITION_DURATION_MS}
    />
    {redAboveLossOfControl && (
      // When the canvas layer already paints the static dither, render the
      // overlay in interactive-only mode so the hovered-band danger labels
      // ("Moderate Risk", "High Risk", …) and band highlight still appear on
      // top of it without re-drawing the whole pattern.
      <RedAboveLossOfControlOverlay
        show={showLossOfControlLines}
        lossOfControlMinPoints={lossOfControlMinPoints}
        lossOfControlMaxPoints={lossOfControlMaxPoints}
        effectiveCappedTimestamp={effectiveCappedTimestamp}
        xForTimestamp={xForTimestamp}
        yForValue={yForValue}
        valueForY={valueForY}
        faded={redAboveLossOfControlFaded}
        interactiveOnly={redAboveLossOfControlCanvasLayerRendered}
        capabilityLineScreenPoints={capabilityLineScreenPoints}
        additionalCapabilityLinePolylines={additionalCapabilityLinePolylines}
        fadingCapabilityLinePolylines={fadingCapabilityLinePolylines}
        plotSpanX={plotSpanX}
      />
    )}
    {riskBackgroundLabels.map(riskBackgroundLabel => (
      <g
        key={`${riskBackgroundLabel.label}-guide`}
        opacity={riskBackgroundLabel.opacity}
        pointerEvents="none"
        style={{ transition: `opacity ${GRAPH_LABEL_FADE_DURATION_MS}ms` }}
      >
        <path
          d={riskBackgroundLabel.guidePathData}
          fill="none"
          stroke={RISK_BACKGROUND_LABEL_GUIDE_STROKE.borderColor}
          strokeWidth={RISK_BACKGROUND_LABEL_GUIDE_STROKE.borderThickness}
          strokeDasharray={RISK_BACKGROUND_LABEL_GUIDE_STROKE.dasharray}
          strokeOpacity={RISK_BACKGROUND_LABEL_GUIDE_STROKE.opacity}
        />
        <path
          d={riskBackgroundLabel.guidePathData}
          fill="none"
          stroke={RISK_BACKGROUND_LABEL_GUIDE_STROKE.color}
          strokeWidth={RISK_BACKGROUND_LABEL_GUIDE_STROKE.thickness}
          strokeDasharray={RISK_BACKGROUND_LABEL_GUIDE_STROKE.dasharray}
          strokeOpacity={RISK_BACKGROUND_LABEL_GUIDE_STROKE.opacity}
        />
      </g>
    ))}
    {riskBackgroundLabels.map(riskBackgroundLabel => (
      <text
        key={riskBackgroundLabel.label}
        x={riskBackgroundLabel.x}
        y={riskBackgroundLabel.y}
        fill={lossOfControlAccentColor}
        opacity={riskBackgroundLabel.opacity}
        fontSize="11"
        dominantBaseline="middle"
        pointerEvents="none"
        style={{
          textShadow: getLabelStroke('var(--playbook-epilogue-bg, #fffff8)'),
          transition: `opacity ${GRAPH_LABEL_FADE_DURATION_MS}ms`,
        }}
      >
        {riskBackgroundLabel.label}
      </text>
    ))}
    {editorMode && (
      <text
        x={4}
        y={11}
        fontSize={9}
        fill="#000"
        pointerEvents="none"
        style={{
          textShadow: getLabelStroke('var(--playbook-epilogue-bg, #fffff8)'),
          letterSpacing: '0.05em',
        }}
      >
        EDIT MODE (⌃E) – {activePlan}
      </text>
    )}
  </>
  )
}
